import React, { useEffect } from 'react'

import i18n from 'i18n-literally'
import PropTypes from 'prop-types'

import { Close as CloseIcon } from '@mui/icons-material'
import { Button, IconButton, Snackbar } from '@mui/material'
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles'
import { makeStyles } from '@mui/styles'

import getGlobal from '~/src/Lib/getGlobal'
import createLogger from '~/src/Lib/Logging'
import { DATE_FORMATS, formattedDate } from '~/src/Lib/Utils'
import EntityPrimaryColor from '~/src/UI/Shared/EntityPrimaryColor'
import Icon from '~/src/UI/Shared/Icon'
import T from '~/src/UI/Shared/Typography'
import { buildThemeFromColor } from '~/src/UI/Theme/mui'

const displayName = 'HarvestPhaseChangeSnackbarNotification'
const logger = createLogger(displayName)

const useStyles = makeStyles({
  phaseChangeBlock: {
    padding: '1rem 0 1rem 1rem',
    width: '32rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    alignItems: 'flex-start',
  },
  snackbarActions: {
    marginLeft: 'auto',
    display: 'flex',
    gap: '1rem',
  },
  hidden: {
    display: 'none',
  },
}, { name: displayName })

const HarvestPhaseChangeSnackbarNotification = ({
  hide = false,
  harvest,
  nextPhase,
  onDismiss,
  onReview,
  onView = null,
}) => {
  const classes = useStyles()

  // if onView prop provided, we need to call it so that user has at least seen the notification, we ensure by detecting mouse moves
  useEffect(() => {
    const handleMouseMove = () => {
      if (onView) {
        onView()
      }
    }
    getGlobal().addEventListener('mousemove', handleMouseMove, { once: true })

    return () => getGlobal().removeEventListener('mousemove', handleMouseMove)
  }, [onView])

  const handleClose = (_, reason) => {
    if (reason === 'clickaway') {
      return
    }
    onDismiss()
  }

  const harvestName = (
    <T.Subtitle color="secondary" component="span">
      {harvest.name}
    </T.Subtitle>
  )

  const nextPhaseName = nextPhase ? (
    <EntityPrimaryColor colorKey={nextPhase.phaseType}>
      {({ primary }) => (
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme => buildThemeFromColor(primary, { adjustColor: true, bg: theme.palette.background.dark })}>
            <T.Subtitle color="primary" component="span">{nextPhase.name}</T.Subtitle>
          </ThemeProvider>
        </StyledEngineProvider>
      )}
    </EntityPrimaryColor>
  ) : null

  const nextPhaseStartDate = nextPhase?.startDate ? `(${formattedDate(nextPhase.startDate, DATE_FORMATS.SHORT_DATETIME)})` : ''

  return (
    <Snackbar
      key="harvest-phase-change-notification"
      open
      data-time-series__cursor-exclude
      classes={{ root: hide ? classes.hidden : '' }}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      message={(
        <div className={classes.phaseChangeBlock}>
          <T.H5>
            {i18n`Upcoming phase change ${nextPhaseStartDate}`}
          </T.H5>
          <T.Subtitle.Lite>
            {i18n`Tomorrow, harvest group `}{harvestName}
            {i18n` is changing to a new phase`}{nextPhaseName ? ': ' : ''}{nextPhaseName}.
            {i18n` The irrigation schedule will be adjusted as defined by the harvest group.`}
          </T.Subtitle.Lite>
          <T.Subtitle.Lite>
            {i18n`Please verify that the new irrigation schedule is correct.`}
          </T.Subtitle.Lite>
          <div className={classes.snackbarActions}>
            <Button
              color="primary"
              variant="outlined"
              onClick={onDismiss}
            >
              {i18n`Dismiss`}
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={onReview}
            >
              {i18n`Verify`}
            </Button>
          </div>
        </div>
      )}
      action={[
        <IconButton
          key="close"
          aria-label="Close"
          color="inherit"
          size="small"
          onClick={handleClose}
        >
          <Icon icon={CloseIcon} />
        </IconButton>,
      ]}
      onClose={handleClose}
    />
  )
}
HarvestPhaseChangeSnackbarNotification.propTypes = {
  hide: PropTypes.bool,
  harvest: PropTypes.shape({
    name: PropTypes.string,
  }).isRequired,
  nextPhase: PropTypes.shape({
    name: PropTypes.string,
    phaseType: PropTypes.string,
    startDate: PropTypes.string,
  }).isRequired,
  onDismiss: PropTypes.func.isRequired,
  onReview: PropTypes.func.isRequired,
  onView: PropTypes.func,
}
HarvestPhaseChangeSnackbarNotification.displayName = displayName

export default React.memo(HarvestPhaseChangeSnackbarNotification)
